import type { IntlShape } from 'react-intl';
import getHelpCenterUrl from '@alltrails/core/utils/getHelpCenterUrl';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import { StatusBadgeProps } from '@alltrails/denali/src/components/StatusBadge/StatusBadge';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import type { User } from '@alltrails/types/User';
import localizedPaths from './localizedPaths';

export type FooterLinksType = {
  href: string;
  title: string;
  amplitudeEventName?: string;
  childLinks: {
    amplitudeEventName?: string;
    href: string;
    openInNewWindow?: boolean;
    title: string;
    badge?: StatusBadgeProps;
  }[];
};

type TranslateRouteCategory = 'trails' | 'parks' | 'cities' | 'regions';

const translateRoute = (languageRegionCode: LanguageRegionCode, category: TranslateRouteCategory): string => {
  const routeMap = localizedPaths[languageRegionCode];
  return (routeMap && routeMap[category]) || category;
};

const exploreLinks = (intl: IntlShape, languageRegionCode: LanguageRegionCode): FooterLinksType => ({
  title: intl.formatMessage({ defaultMessage: 'Explore' }),
  href: wrapUrlSafe('/explore', languageRegionCode),
  childLinks: [
    {
      title: intl.formatMessage({ defaultMessage: 'Countries' }),
      href: wrapUrlSafe('/directory/countries', languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Regions' }),
      href: wrapUrlSafe(`/directory/${translateRoute(languageRegionCode, 'regions')}/A/1`, languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Cities' }),
      href: wrapUrlSafe(`/directory/${translateRoute(languageRegionCode, 'cities')}/A/1`, languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Parks' }),
      href: wrapUrlSafe(`/directory/${translateRoute(languageRegionCode, 'parks')}/A/1`, languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Trails' }),
      href: wrapUrlSafe(`/directory/${translateRoute(languageRegionCode, 'trails')}/A/1`, languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Points of Interest' }),
      href: wrapUrlSafe(`/directory/poi/A/1`, languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Trail Features' }),
      href: wrapUrlSafe(`/directory/trail-features`, languageRegionCode)
    }
  ]
});

const mapLinks = ({
  languageRegionCode,
  intl,
  currentUser
}: {
  languageRegionCode: LanguageRegionCode;
  intl: IntlShape;
  currentUser?: User | null;
}): FooterLinksType => ({
  title: intl.formatMessage({ defaultMessage: 'Custom routes & maps' }),
  href: currentUser
    ? wrapUrlSafe(`/members/${currentUser.slug}/custom-routes`, languageRegionCode)
    : // 'user-custom-routes=true' is used to redirect the user to their custom routes page after they authenticate:
      wrapUrlSafe(`/signup?returnTo=${encodeURIComponent('/explore?user-custom-routes=true')}`, languageRegionCode),
  childLinks: [
    {
      title: intl.formatMessage({ defaultMessage: 'Build custom route' }),
      // 'trigger=footer' is used to pass the correct trigger and upsell trigger to the SignUpModal
      href: wrapUrlSafe('/explore/custom-routes/new?trigger=footer', languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Print maps' }),
      href: currentUser?.featureEntitlements?.includes('print_maps')
        ? wrapUrlSafe('/maps/print', languageRegionCode)
        : wrapUrlSafe('/plus', languageRegionCode)
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Route converter' }),
      href: wrapUrlSafe('/converter', languageRegionCode)
    }
  ]
});

const companyLinks = (languageRegionCode: LanguageRegionCode, intl: IntlShape): FooterLinksType => ({
  href: wrapUrlSafe('/about', languageRegionCode),
  title: intl.formatMessage({ defaultMessage: 'Company' }),
  childLinks: [
    {
      href: wrapUrlSafe('/about', languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'About' })
    },
    {
      href: wrapUrlSafe('/careers', languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'Jobs' })
    },
    {
      href: wrapUrlSafe('/press', languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'News' })
    },
    {
      href: wrapUrlSafe('/ambassadors', languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'Ambassadors' })
    },
    {
      href: wrapUrlSafe('/affiliates', languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'Affiliates' })
    }
  ]
});

const communityLinks = (languageRegionCode: LanguageRegionCode, intl: IntlShape, irclickid?: string): FooterLinksType => ({
  href: wrapUrlSafe('/community', languageRegionCode),
  title: intl.formatMessage({ defaultMessage: 'Community' }),
  amplitudeEventName: 'web global footer community clicked',
  childLinks: [
    {
      amplitudeEventName: 'support footer clicked',
      href: getHelpCenterUrl(languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'Support' })
    },
    {
      amplitudeEventName: 'give pro footer clicked',
      href: wrapUrlSafe('/gift', languageRegionCode),
      title: intl.formatMessage({ defaultMessage: 'Gift membership' })
    },
    {
      amplitudeEventName: 'gear footer clicked',
      href: `https://shop.alltrails.com/${irclickid ? `?${new URLSearchParams({ irclickid })}` : ''}`,
      openInNewWindow: true,
      title: intl.formatMessage({ defaultMessage: 'AllTrails Gear' })
    }
  ]
});

const footerLinks = ({
  intl,
  languageRegionCode,
  user,
  irclickid
}: {
  intl: IntlShape;
  languageRegionCode: LanguageRegionCode;
  user?: User | null;
  irclickid?: string;
}): FooterLinksType[] => [
  exploreLinks(intl, languageRegionCode),
  mapLinks({ languageRegionCode, intl, currentUser: user }),
  companyLinks(languageRegionCode, intl),
  communityLinks(languageRegionCode, intl, irclickid)
];

export default footerLinks;
